<i18n>
  {
    "fr": {
      "departure-date": "Date de départ",
      "return-date": "Date de retour",
      "errors": {
        "departure-date-required": "La date de départ est requise",
        "return-date-required": "La date de retour est requise"
      }
    },
    "en": {
      "departure-date": "Departure date",
      "return-date": "Return date",
      "errors": {
        "departure-date-required": "Departure date is required",
        "return-date-required": "Return date is required"
      }
    }
  }
</i18n>

<template>
  <v-row no-gutters>
    <v-col>
      <v-menu
        ref="menu"
        v-model="outwardMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290"
        @update:return-value="outwardDateTouch"
      >
        <template #activator="{on}">
          <v-text-field
            id="outwardDateTextField"
            v-model="formatOutwardDate"
            data-cy="flight-date-picker-outward-text-field"
            class="my-0 outward py-0"
            color="primary"
            background-color="primaryLight"
            solo
            flat
            :label="$t('departure-date')"
            :error-messages="outwardDateErrors"
            readonly
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="outwardDate"
          data-cy="flight-date-picker-outward"
          :allowed-dates="outwardAllowedDates"
          :locale="localLang"
          :picker-date="outwardPickerDate"
          first-day-of-week="1"
          no-title
          scrollable
          @input="outwardMenu = false"
          @update:picker-date="outwardPickerDate = $event"
        />
      </v-menu>
    </v-col>

    <v-divider
      vertical
      class="divider"
    />

    <v-col>
      <v-menu
        ref="menu"
        v-model="returnMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290"
        @update:return-value="returnDateTouch"
      >
        <template #activator="{on}">
          <v-text-field
            id="returnDateTextField"
            v-model="formatReturnDate"
            data-cy="flight-date-picker-return-text-field"
            class="my-0 py-0 return"
            color="primary"
            :background-color="returnDatePickerColor"
            solo
            flat
            :label="$t('return-date')"
            :error-messages="returnDateErrors"
            :disabled="disableReturnDatePicker"
            readonly
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="returnDate"
          data-cy="flight-date-picker-return"
          :allowed-dates="returnAllowedDates"
          :locale="localLang"
          :disabled="disableReturnDatePicker"
          :picker-date="returnPickerDate"
          first-day-of-week="1"
          no-title
          scrollable
          @input="returnMenu = false"
          @update:picker-date="returnPickerDate = $event"
        />
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, requiredUnless } from 'vuelidate/lib/validators'
import moment from 'moment'

export default {
  name: 'FlightDatePicker',
  mixins: [validationMixin],
  data: () => ({
    outwardMenu: false,
    outwardPickerDate: undefined,
    returnMenu: false,
    returnPickerDate: undefined
  }),

  computed: {
    ...mapState({
      outwardDateState: state => state.fb.searchParameters.outwardDate,
      returnDateState: state => state.fb.searchParameters.returnDate,
      travelTypeState: state => state.fb.searchParameters.travelType
    }),
    /**
     * This computed format the local language to a different ISO, because vuetify date picker need this format
     * @return {string}
     */
    localLang () {
      let lang = ''
      if (this.$i18n.locale === 'fr') {
        lang = 'fr-FR'
      } else if (this.$i18n.locale === 'en') {
        lang = 'en-GB'
      }
      return lang
    },
    outwardDate: {
      /**
       * Just return vueX store state
       * @return {string}
       */
      get () {
        return this.outwardDateState
      },
      /**
       * Call to a vueX store commit method
       * @param newValue {string}
       */
      set (newValue) {
        this.changeOutwardDate(newValue)
      }
    },
    returnDate: {
      /**
       * Just return vueX store state
       * @return {string}
       */
      get () {
        return this.returnDateState
      },
      /**
       * Call to a vueX store commit method
       * @param newValue {string}
       */
      set (newValue) {
        this.changeReturnDate(newValue)
      }
    },
    /**
     * Call to formatDate method for outward date
     * @return {string}
     */
    formatOutwardDate () {
      return this.formatDate(this.outwardDate)
    },
    /**
     * Call to formatDate method for return date
     * @return {string}
     */
    formatReturnDate () {
      return this.formatDate(this.returnDate)
    },
    /**
     * Return a boolean based on travel type state.
     * True if travel type is one-way, false instead.
     * @return {boolean}
     */
    disableReturnDatePicker () {
      let isDisabled
      if (this.travelTypeState === 'one-way') {
        isDisabled = true
        this.changeReturnDate('')
      } else if (this.travelTypeState === 'two-way') {
        isDisabled = false
      }
      return isDisabled
    },
    /**
     * Change the color of the return date text input based travel type state
     * @return {string}
     */
    returnDatePickerColor () {
      let color
      if (this.travelTypeState === 'one-way') {
        color = 'grey lighten-2'
      } else if (this.travelTypeState === 'two-way') {
        color = 'primaryLight'
      }
      return color
    },
    /**
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    isValid () {
      return !this.$v.$invalid
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    outwardDateErrors () {
      const errors = []
      if (!this.$v.outwardDate.$dirty) {
        return errors
      }
      !this.$v.outwardDate.required && errors.push(this.$i18n.t('errors.departure-date-required'))
      return errors
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    returnDateErrors () {
      const errors = []
      if (!this.$v.returnDate.$dirty) {
        return errors
      }
      !this.$v.returnDate.required && errors.push(this.$i18n.t('errors.return-date-required'))
      return errors
    }
  },

  watch: {
    /**
     * Just call to onChangeOutwardDate method when outward date change.
     */
    outwardDate () {
      this.onChangeOutwardDate()
    },
    /**
     * Watch for value change of validity then commit the new value to the store
     * (based on isDeparture prop)
     * @param newVal {boolean}
     */
    isValid (newVal) {
      this.changeCalendarValidity(newVal)
    }
  },
  validations: {
    outwardDate: {
      required
    },
    returnDate: {
      required: requiredUnless(function () {
        return this.disableReturnDatePicker
      })
    }
  },
  created () {
    this.outwardPickerDate = moment().add(2, 'days').format('YYYY-MM-DD')
    this.returnPickerDate = moment().add(2, 'days').format('YYYY-MM-DD')
  },
  methods: {
    ...mapMutations({
      changeOutwardDate: 'fb/searchParameters/changeOutwardDate',
      changeReturnDate: 'fb/searchParameters/changeReturnDate',
      changeCalendarValidity: 'fb/searchParameters/changeCalendarValidity'
    }),
    /**
     * Change return date and the allowed return date in the date picker based on the new outward date.
     * Or it can only change the allowed return date if outward date is sooner than the return date.
     */
    onChangeOutwardDate () {
      if (moment(this.outwardDate) > moment(this.returnDate)) {
        this.returnDate = moment(this.outwardDate).format('YYYY-MM-DD')
        this.returnPickerDate = moment(this.outwardDate).format('YYYY-MM-DD')
      } else {
        this.returnPickerDate = moment(this.outwardDate).format('YYYY-MM-DD')
      }
    },
    /**
     * This method is used by vuetify date picker to know which day is disabled,
     * it take a date as an input (provided by the date picker component)
     * and then test a predicate to return the validity of this date
     * @param val {string} date
     * @return {boolean}
     */
    outwardAllowedDates (val) {
      const date = moment(val)
      const today = moment()
      today.add(2, 'days')
      return date > today
    },
    /**
     * This method is used by vuetify date picker to know which day is disabled,
     * it take a date as an input (provided by the date picker component)
     * and then test a predicate to return the validity of this date
     * @param val {string} date
     * @return {boolean}
     */
    returnAllowedDates (val) {
      const x = moment(val)

      if (this.outwardDate) {
        return x >= moment(this.outwardDate)
      } else {
        const today = moment()
        today.add(2, 'days')
        return x > today
      }
    },
    /**
     * Format the date based on i18n locale
     * @param date {string}
     * @return {string|null}
     */
    formatDate (date) {
      if (date === '') {
        return null
      } else {
        const formatDate = moment(date, 'YYYY-MM-DD')

        if (this.$i18n.locale === 'fr') {
          formatDate.locale('fr')
          return formatDate.format('ddd D/MM/YY')
        } else if (this.$i18n.locale === 'en') {
          formatDate.locale('en')
          return formatDate.format('ddd DD/MM/YY')
        }
      }
    },
    /**
     * Shortcut for vuelidate touch method
     */
    outwardDateTouch () {
      this.$v.outwardDate.$touch()
    },
    /**
     * Shortcut for vuelidate touch method
     */
    returnDateTouch () {
      this.$v.returnDate.$touch()
    }
  }
}
</script>

<style scoped>
  .v-input.return >>> .v-input__slot {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .v-input.outward >>> .v-input__slot {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .divider {
    height: 48px;
    border-color: #bec5ce;
  }
</style>
